import { movieCatalog, editorPicks } from '@/mocks/movies';
import type { Movie } from '@/pages/home/types';
import SectionHeading from '@/pages/home/components/SectionHeading';

interface FeaturedSectionProps {
  onPickGenre: (genre: string) => void;
}

export default function FeaturedSection({ onPickGenre }: FeaturedSectionProps) {
  const featured = (movieCatalog as Movie[]).filter((m) => m.featured).slice(0, 4);

  return (
    <section id="featured" className="bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="编辑精选"
          title="好看的电影推荐：先从这几部开始"
          desc="编辑团队从片库中挑出题材与口碑都在线的一批作品，覆盖硬科幻、古装悬疑、战争史诗与家庭剧情，适合作为选片的第一站。"
        />

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {featured.map((m) => (
              <article
                key={m.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
                  <img
                    src={m.poster}
                    alt={`${m.title} 剧照`}
                    title={`${m.title} - 555电影精选推荐`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-1 text-xs font-medium text-background-50">
                    {m.rating} 分
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                    <span>{m.year}</span>
                    <span>·</span>
                    <span>{m.region}</span>
                    <span>·</span>
                    <span>{m.duration}</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{m.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{m.blurb}</p>
                  <button
                    type="button"
                    onClick={() => onPickGenre(m.genre)}
                    className="mt-auto flex w-fit cursor-pointer items-center gap-1 pt-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                  >
                    查看同类 {m.genre} 片单
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>

          <aside className="flex flex-col gap-4 rounded-lg border border-background-200 bg-secondary-50 p-6">
            <h3 className="font-heading text-lg font-bold text-foreground-950">片单观察笔记</h3>
            <p className="text-sm leading-relaxed text-foreground-600">
              除了评分，我们还关心一部片子的题材完成度与可看性。下面几条笔记来自编辑团队的片库梳理。
            </p>
            <div className="flex flex-col divide-y divide-background-200">
              {editorPicks.map((pick, index) => (
                <div key={pick.title} className="flex gap-4 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-background-50">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-accent-700">{pick.tag}</span>
                    <h4 className="text-sm font-bold text-foreground-950">
                      <a href="#library" className="hover:text-primary-700">
                        {pick.title}
                      </a>
                    </h4>
                    <p className="text-xs leading-relaxed text-foreground-600">{pick.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}